/**
 * BUILD on Chinese, Japanese, Korean and Arabic, FROM THE COURSE THE PLAYER
 * ALREADY HOLDS (job #635; Tom 2026-10-29, r-2026-10-29: "We already have the
 * LEGO Tile Assembly for Chinese Japanese and Korean. Arabic too actually. So we
 * don't have to do anything at all. We just use that. And the pods are already
 * split into chunks.").
 *
 * No deck is built for these courses and none is shipped. The Build card's deck
 * is derived on the device from the course bundle the player has already loaded:
 * the LEGOs, the seeds and the pod-1 lines, run through the same pure builder the
 * static decks come from (welshDeck.buildWelshDeck), in the native script.
 *
 * THE TILES are the main player's own LEGO tile assembly (utils/decomposePhrase,
 * the per-character CJK tiler LegoAssembly renders from). Chinese and Japanese
 * write no spaces, so each seed and pod line is first cut into the course's LEGOs
 * by that tiler and the cut is written as spaces, which is the one unit the Build
 * engine reads (wordsOf). The card puts the line back together without them
 * (unspacedShown). Korean and Arabic are spaced and go to the builder as written.
 *
 * A Chinese or Japanese line the LEGOs do not tile whole is left out, never shown
 * as single characters: Build is putting LEGOs together, not spelling.
 *
 * Pure: bundle in, deck out.
 */
import type { CourseBundle } from '@ssi/core'
import { decomposePhrase } from '../../utils/decomposePhrase'
import { buildWelshDeck, type LegoRow, type PodRow, type SeedRow, type WelshDeckFile } from './welshDeck'

/** The non-Roman targets whose Build card is read off the bundle. */
export const BUNDLE_BUILD_LANGS: readonly string[] = ['zho', 'jpn', 'kor', 'ara']
/** The ones written without spaces between words. */
export const UNSPACED_LANGS: readonly string[] = ['zho', 'jpn']
/**
 * Pod lines held back, the taste-safe default until Aran rules: the Arabic pod-1
 * is largely colloquial and unvowelled where the course's LEGOs are vowelled
 * standard Arabic, so its lines would build from words the course never taught.
 */
export const POD_HELD_LANGS: readonly string[] = ['ara']

const lang3 = (s: string | null | undefined): string => (s ?? '').toLowerCase().slice(0, 3)

export const isBundleBuildLang = (lang: string | null | undefined): boolean => BUNDLE_BUILD_LANGS.includes(lang3(lang))

/** Does this course get its Build card from its bundle? Only where the course's own target is one of the four. */
export const bundleBuildFor = (targetLang: string | null | undefined, courseCode: string | null | undefined): boolean => {
  const lang = lang3(targetLang || courseCode?.split('_')[0])
  return !!courseCode && isBundleBuildLang(lang) && lang3(courseCode) === lang
}

/** Sentence punctuation the tiler does not know: dropped before cutting an unspaced line. */
const PUNCT = /[\p{P}\p{S}\s]+/gu

/**
 * Cut an unspaced line into the course's LEGOs, longest first, with the player's
 * own tiler. Null when any character is left over — the line is not LEGO-built.
 */
export function cutByLegos(text: string, textMap: Map<string, string>): string[] | null {
  return cutWithCharacters(text, textMap, new Set([...textMap.values()].join('')))
}

function cutWithCharacters(text: string, textMap: Map<string, string>, chars: ReadonlySet<string>): string[] | null {
  const bare = text.replace(PUNCT, '')
  if (!bare) return null
  // A character no LEGO carries cannot be tiled: settle it here, before the tiler warns once per character.
  if ([...bare].some(ch => !chars.has(ch))) return null
  const blocks = decomposePhrase({ targetText: bare, salientId: '', textMap, componentsByLegoId: new Map() })
  if (!blocks?.length || blocks.some(b => b.id.startsWith('_gap'))) return null
  if (blocks.map(b => b.targetText).join('') !== bare) return null
  return blocks.map(b => b.targetText)
}

/** The native script, which these four courses always read in; a row without it is left out. */
const native = (n: string | null | undefined): string | null => (n ?? '').trim() || null

export function bundleBuildDeck(bundle: Pick<CourseBundle, 'courseCode' | 'legos' | 'seeds' | 'pods'>, targetLang?: string | null): WelshDeckFile {
  const lang = lang3(targetLang || bundle.courseCode.split('_')[0])
  const unspaced = UNSPACED_LANGS.includes(lang)
  const squash = (t: string | null) => (t && unspaced ? t.replace(/\s+/g, '') : t)

  const legos: LegoRow[] = bundle.legos.map(l => ({
    seed_number: l.seedNumber, lego_index: l.legoIndex, known_text: l.knownText, target_text: squash(native(l.targetTextNative)),
  }))
  // Only unspaced lines use the tiler and need its LEGO/part vocabulary.
  const textMap = new Map<string, string>()
  if (unspaced) for (const l of bundle.legos) {
    const t = squash(native(l.targetTextNative))
    if (t) textMap.set(l.legoId, t)
    for (const c of l.components ?? []) if (c.target && !textMap.has(`atom:${l.legoId}:${c.target}`)) textMap.set(`atom:${l.legoId}:${c.target}`, c.target.replace(/\s+/g, ''))
  }
  // The vocabulary is fixed for this deck: share its character guard across all lines.
  // Seeds and pod lines can repeat; their tiling depends only on this deck's vocabulary.
  const cuts = new Map<string, string | null>()
  let chars: Set<string> | undefined
  const cut = (t: string | null): string | null => {
    if (!t || !unspaced) return t
    if (cuts.has(t)) return cuts.get(t)!
    chars ??= new Set([...textMap.values()].join(''))
    const result = cutWithCharacters(t, textMap, chars)?.join(' ') ?? null
    cuts.set(t, result)
    return result
  }

  const seeds: SeedRow[] = bundle.seeds.map(s => ({ seed_number: s.seedNumber, known_text: s.knownText ?? null, target_text: cut(native(s.targetTextNative)) }))
  const pod = POD_HELD_LANGS.includes(lang) ? undefined : bundle.pods?.[0]
  const podRows: PodRow[] = (pod?.sentences ?? []).map(p => ({ global_order: p.globalOrder, known_text: p.knownText, target_text: cut(native(p.targetText)) }))
  return buildWelshDeck(bundle.courseCode, seeds, legos, podRows, pod?.podId ?? null).deck
}

/** A Chinese or Japanese line as read: the LEGO cut taken back out. Everything else as written. */
export const unspacedShown = (lang: string | null | undefined, text: string): string =>
  UNSPACED_LANGS.includes(lang3(lang)) ? text.replace(/\s+/g, '') : text
